import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie,
} from 'recharts';
import { Download, ArrowLeft, Sparkles, RefreshCw, X, SlidersHorizontal, Columns, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useReturnsData, Row } from './returnsDataCache';
import './ReturnsAI.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const CHART_COLORS = ['#7aa2ff','#57d38c','#ffd166','#c084fc','#ff6b6b','#64d2ff','#fb923c','#34d399','#f472b6','#38bdf8'];
const ACTIVE_STATUSES = ['ACTIVE', 'FINANCIAL', 'PROSPECT'];
const MILESTONE_COLORS: Record<string, string> = {
  'Not Started - Phase 0': '#8b949e',
  'Prep - Phase 1':          '#7aa2ff',
  'Review - Phase 2':        '#ffd166',
  'Member Review - Phase 3': '#c084fc',
  'Finalization - Phase 4':  '#57d38c',
};
const MILESTONE_CLASS: Record<string, string> = {
  'Not Started - Phase 0': 'milestone-0',
  'Prep - Phase 1':        'milestone-1',
  'Review - Phase 2':      'milestone-2',
  'Member Review - Phase 3': 'milestone-3',
  'Finalization - Phase 4':  'milestone-4',
};
const MILESTONE_ORDER = [
  'Not Started - Phase 0','Prep - Phase 1','Review - Phase 2',
  'Member Review - Phase 3','Finalization - Phase 4',
];
const PAGE_SIZE = 100;

// ── Types ─────────────────────────────────────────────────────────────────────

type Filter = { id: string; label: string; fn: (r: Row) => boolean };

type PaneState = {
  milestone: string[];
  returnType: string[];
  dueDate: string[];
  clientStatus: string[];
  prepProcess: string[];
  partnerSearch: string;
  mselMissing: boolean;
  sowMissing: boolean;
  accrualOnly: boolean;
  vipOnly: boolean;
  iddBeforeStatutory: boolean;
};

const EMPTY_PANE: PaneState = {
  milestone: [], returnType: [], dueDate: [], clientStatus: [],
  prepProcess: [], partnerSearch: '', mselMissing: false,
  sowMissing: false, accrualOnly: false, vipOnly: false,
  iddBeforeStatutory: false,
};

type ColDef = {
  key: string; label: string; default: boolean;
  render?: (r: Row) => React.ReactNode;
};

// ── All columns ───────────────────────────────────────────────────────────────

const ALL_COLS: ColDef[] = [
  // Default visible
  { key: 'ClientName',    label: 'Client',         default: true,
    render: r => <a href={String(r.clientLink ?? '#')} target="_blank" rel="noreferrer">{String(r.ClientName ?? '—')}</a> },
  { key: 'Job_Name',      label: 'Job',            default: true,
    render: r => <a href={String(r.jobLink ?? '#')} target="_blank" rel="noreferrer">{String(r.Job_Name ?? '—')}</a> },
  { key: 'ReturnType',    label: 'Type',           default: true },
  { key: 'milestone',     label: 'Phase',          default: true,
    render: r => { const m = String(r.milestone ?? ''); return <span className={`milestone-badge ${MILESTONE_CLASS[m] ?? 'milestone-0'}`}>{m || '—'}</span>; } },
  { key: 'JobWorkStatus', label: 'Work Status',    default: true },
  { key: 'ClientStatus',  label: 'Client Status',  default: true },
  { key: 'JobPartner',    label: 'Partner',        default: true },
  { key: 'JobManager',    label: 'Manager',        default: true },
  { key: 'CurrentStaff',  label: 'Current Staff',  default: true },
  { key: 'CurrentDueDate',label: 'Due Date',       default: true, render: r => fmtDate(r.CurrentDueDate) },
  { key: 'PrepHours',     label: 'Prep Hrs / Bgt', default: true, render: r => fmtHours(r.PrepHours, r.PrepBudget) },
  { key: 'ReviewHours',   label: 'Rev Hrs / Bgt',  default: true, render: r => fmtHours(r.ReviewHours, r.ReviewBudget) },
  { key: 'MSEL',          label: 'MSEL',           default: true,
    render: r => r.MSEL === 'MISSING' ? <span className="flag-missing">MISSING</span> : <span className="flag-ok">✓</span> },
  { key: 'SOW',           label: 'SOW',            default: true,
    render: r => r.SOW  === 'MISSING' ? <span className="flag-missing">MISSING</span> : <span className="flag-ok">✓</span> },
  { key: 'PrepProcess',   label: 'Prep Process',   default: true },
  { key: 'ClientOffice',  label: 'Office',         default: true },
  // Additional (opt-in)
  { key: 'Hours',             label: 'Total WIP Hrs',          default: false },
  { key: 'Realization',       label: 'Realization %',          default: false,
    render: r => r.Realization != null ? `${(Number(r.Realization) * 100).toFixed(0)}%` : '—' },
  { key: 'isAccrual',         label: 'Accrual',                default: false },
  { key: 'isVIP',             label: 'VIP',                    default: false },
  { key: 'is_initial_return', label: 'Initial Return',         default: false },
  { key: 'IDD',               label: 'Internal Due Date',      default: false, render: r => fmtDate(r.IDD) },
  { key: 'FedJurisdictionStatus', label: 'Fed Jurisdiction',   default: false },
  { key: 'ExtensionStatusFed',label: 'Extension Status',       default: false },
  { key: 'ReturnStatusFed',   label: 'Return Status',          default: false },
  { key: 'ConsReturn',        label: 'Consolidated Return',    default: false },
  { key: 'returnId',          label: 'Return ID',              default: false },
  { key: 'PY_Hours',          label: 'PY Total Hrs',           default: false },
  { key: 'PY_PrepHours',      label: 'PY Prep Hrs',            default: false },
  { key: 'PY_ReviewHours',    label: 'PY Rev Hrs',             default: false },
  { key: 'PY_Realization',    label: 'PY Realization %',       default: false,
    render: r => r.PY_Realization != null ? `${(Number(r.PY_Realization) * 100).toFixed(0)}%` : '—' },
  { key: 'Job_Created',       label: 'Job Created',            default: false, render: r => fmtDate(r.Job_Created) },
];

const DEFAULT_COL_KEYS = new Set(ALL_COLS.filter(c => c.default).map(c => c.key));

// ── Helpers ───────────────────────────────────────────────────────────────────

function groupCount(rows: Row[], col: string, topN = 25) {
  const map: Record<string, number> = {};
  for (const r of rows) { const v = String(r[col] ?? 'Unknown'); map[v] = (map[v] || 0) + 1; }
  return Object.entries(map).map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count).slice(0, topN);
}

function fmtDate(val: unknown): string {
  if (!val) return '—';
  return String(val).split('T')[0];
}

function fmtHours(hrs: unknown, budget: unknown): React.ReactNode {
  const h = Number(hrs) || 0;
  const b = Number(budget) || 0;
  const over = b > 0 && h > b;
  const label = b > 0 ? `${h.toFixed(1)} / ${b.toFixed(1)}` : h > 0 ? h.toFixed(1) : '—';
  return <span className={over ? 'over-budget' : 'on-budget'}>{label}</span>;
}

// Only considers the 15th of each month as a statutory deadline
function nextDueDate(rows: Row[]): string | null {
  const today = new Date().toISOString().split('T')[0];
  const dates = [...new Set(
    rows.map(r => fmtDate(r.CurrentDueDate))
        .filter(d => d !== '—' && d >= today && d.split('-')[2] === '15')
  )].sort();
  return dates[0] ?? null;
}

function renderMarkdown(md: string): string {
  const esc = md.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const lines = esc.split('\n');
  const out: string[] = [];
  let inList = false;
  const inline = (t: string) =>
    t.replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')
     .replace(/\*(.+?)\*/g,'<em>$1</em>')
     .replace(/`(.+?)`/g,'<code>$1</code>');
  for (const line of lines) {
    const h = line.match(/^(#{1,3}) (.+)$/);
    const li = line.match(/^[-*•] (.+)$/);
    if (h) {
      if (inList) { out.push('</ul>'); inList = false; }
      const lvl = Math.min(h[1].length + 3, 6);
      out.push(`<h${lvl}>${inline(h[2])}</h${lvl}>`);
    } else if (li) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${inline(li[1])}</li>`);
    } else if (line.trim() === '') {
      if (inList) { out.push('</ul>'); inList = false; }
    } else {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<p>${inline(line)}</p>`);
    }
  }
  if (inList) out.push('</ul>');
  return out.join('');
}

function buildInsightPayload(rows: Row[], filterLabels: string[]) {
  const typeGroups: Record<string, Row[]> = {};
  for (const r of rows) { const k = String(r.ReturnType ?? 'Other'); (typeGroups[k] = typeGroups[k] ?? []).push(r); }
  const avgPrepByType = Object.fromEntries(
    Object.entries(typeGroups).map(([k, v]) => [
      k,
      +(v.reduce((s, r) => s + (Number(r.PrepHours) || 0), 0) / v.length).toFixed(1),
    ])
  );
  return {
    total: rows.length,
    filters_applied: filterLabels,
    by_milestone: MILESTONE_ORDER.map(m => ({ label: m, count: rows.filter(r => r.milestone === m).length })),
    by_return_type: groupCount(rows, 'ReturnType'),
    by_partner_top10: groupCount(rows, 'JobPartner', 10),
    by_manager_top10: groupCount(rows, 'JobManager', 10),
    by_prep_process: groupCount(rows, 'PrepProcess'),
    by_office: groupCount(rows, 'ClientOffice'),
    msel_missing: rows.filter(r => r.MSEL === 'MISSING').length,
    sow_missing:  rows.filter(r => r.SOW  === 'MISSING').length,
    accrual_count: rows.filter(r => r.isAccrual === 'TRUE').length,
    vip_count: rows.filter(r => r.isVIP === 'TRUE').length,
    over_prep_budget: rows.filter(r => Number(r.PrepHours) > Number(r.PrepBudget) && Number(r.PrepBudget) > 0).length,
    over_review_budget: rows.filter(r => Number(r.ReviewHours) > Number(r.ReviewBudget) && Number(r.ReviewBudget) > 0).length,
    no_hours: rows.filter(r => !r.Hours || Number(r.Hours) === 0).length,
    avg_prep_hours_by_type: avgPrepByType,
    due_dates: groupCount(rows, 'CurrentDueDate', 10)
      .map(r => ({ date: fmtDate(r.label), count: r.count }))
      .sort((a, b) => a.date.localeCompare(b.date)),
  };
}

const INSIGHTS_SYSTEM = `You are a tax practice analytics assistant at BMSS. Analyze the following aggregate statistics from the returns management system.
Return ONLY a clean markdown response with key insights, observations, and action items.
Use ## section headers, bold key numbers, and bullet points. Aim for 400-600 words.
Focus on: workload distribution, risk areas (missing signatures, over-budget, stalled returns), partner/manager balance, and process efficiency.`;

// ── Filter pane component ─────────────────────────────────────────────────────

function CheckGroup({
  label, options, selected, onChange,
}: { label: string; options: string[]; selected: string[]; onChange: (v: string[]) => void }) {
  if (options.length === 0) return null;
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  return (
    <div className="pane-section">
      <div className="pane-section-title">{label}</div>
      {options.map(opt => (
        <label key={opt} className="pane-check">
          <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
          {opt}
        </label>
      ))}
    </div>
  );
}

function FilterPane({
  pane, setPane, fullDataset,
}: { pane: PaneState; setPane: React.Dispatch<React.SetStateAction<PaneState>>; fullDataset: Row[] }) {
  const set = <K extends keyof PaneState>(k: K, v: PaneState[K]) => setPane(p => ({ ...p, [k]: v }));

  const distinct = (col: string) =>
    [...new Set(fullDataset.map(r => String(r[col] ?? '')).filter(Boolean))].sort();

  const dueDates15 = useMemo(() =>
    [...new Set(fullDataset.map(r => fmtDate(r.CurrentDueDate)).filter(d => d !== '—' && d.split('-')[2] === '15'))].sort()
  , [fullDataset]);

  return (
    <div className="rai-filter-pane">
      <div className="pane-header">
        <span>Filters</span>
        <button className="btn" style={{ fontSize: 11, padding: '2px 8px' }}
          onClick={() => setPane(EMPTY_PANE)}>Clear all</button>
      </div>

      <div className="pane-body">
        <div className="pane-section">
          <div className="pane-section-title">Partner search</div>
          <input className="pane-search" placeholder="Search partner…"
            value={pane.partnerSearch} onChange={e => set('partnerSearch', e.target.value)} />
        </div>

        <CheckGroup label="Phase" options={MILESTONE_ORDER} selected={pane.milestone}
          onChange={v => set('milestone', v)} />

        <CheckGroup label="Return Type"
          options={distinct('ReturnType')}
          selected={pane.returnType} onChange={v => set('returnType', v)} />

        {dueDates15.length > 0 && (
          <CheckGroup label="Statutory Due Date (15th)"
            options={dueDates15} selected={pane.dueDate} onChange={v => set('dueDate', v)} />
        )}

        <CheckGroup label="Client Status" options={distinct('ClientStatus')}
          selected={pane.clientStatus} onChange={v => set('clientStatus', v)} />

        <CheckGroup label="Prep Process" options={distinct('PrepProcess')}
          selected={pane.prepProcess} onChange={v => set('prepProcess', v)} />

        <div className="pane-section">
          <div className="pane-section-title">Flags</div>
          {([
            ['mselMissing',        'MSEL Missing'],
            ['sowMissing',         'SOW Missing'],
            ['accrualOnly',        'Accrual Only'],
            ['vipOnly',            'VIP Only'],
            ['iddBeforeStatutory', 'IDD Before Statutory Due'],
          ] as [keyof PaneState, string][]).map(([k, lbl]) => (
            <label key={k} className="pane-check">
              <input type="checkbox" checked={!!pane[k]}
                onChange={e => set(k, e.target.checked as PaneState[typeof k])} />
              {lbl}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Column chooser component ──────────────────────────────────────────────────

function ColChooser({ visibleKeys, setVisibleKeys, onClose }: {
  visibleKeys: Set<string>;
  setVisibleKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
  onClose: () => void;
}) {
  const toggle = (key: string) =>
    setVisibleKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const defaults = ALL_COLS.filter(c => c.default);
  const extras   = ALL_COLS.filter(c => !c.default);
  return (
    <div className="col-chooser-panel">
      <div className="col-chooser-header">
        <span>Columns</span>
        <button className="btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={onClose}>Done</button>
      </div>
      <div className="col-chooser-section-title">Default columns</div>
      <div className="col-chooser-grid">
        {defaults.map(c => (
          <label key={c.key} className="pane-check">
            <input type="checkbox" checked={visibleKeys.has(c.key)} onChange={() => toggle(c.key)} />
            {c.label}
          </label>
        ))}
      </div>
      <div className="col-chooser-section-title" style={{ marginTop: 10 }}>Additional columns</div>
      <div className="col-chooser-grid">
        {extras.map(c => (
          <label key={c.key} className="pane-check">
            <input type="checkbox" checked={visibleKeys.has(c.key)} onChange={() => toggle(c.key)} />
            {c.label}
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ReturnsAI() {
  const navigate = useNavigate();
  const { rows: fullDataset, loading: dataLoading, error: dataError, reload } = useReturnsData();

  const [defaultFilters, setDefaultFilters] = useState<Filter[]>([]);
  const [pane, setPane]                     = useState<PaneState>(EMPTY_PANE);
  const [paneOpen, setPaneOpen]             = useState(false);
  const [visibleColKeys, setVisibleColKeys] = useState<Set<string>>(new Set(DEFAULT_COL_KEYS));
  const [colChooserOpen, setColChooserOpen] = useState(false);
  const [sortCol, setSortCol]               = useState('CurrentDueDate');
  const [sortDir, setSortDir]               = useState<'asc'|'desc'>('asc');
  const [page, setPage]                     = useState(1);
  const [insightsHtml, setInsightsHtml]     = useState<string | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsOpen, setInsightsOpen]     = useState(false);

  // Build default filters once data loads
  useEffect(() => {
    if (dataLoading || fullDataset.length === 0) return;
    const activeFilter: Filter = {
      id: 'active-status',
      label: 'Active clients & jobs',
      fn: r => ACTIVE_STATUSES.includes(String(r.ClientStatus ?? '')) && ![2, 3].includes(Number(r.Job_Status)),
    };
    const nd = nextDueDate(fullDataset.filter(activeFilter.fn));
    const dueDateFilter: Filter | null = nd ? {
      id: 'due-date',
      label: `Due: ${nd}`,
      fn: r => fmtDate(r.CurrentDueDate) === nd,
    } : null;
    setDefaultFilters(dueDateFilter ? [activeFilter, dueDateFilter] : [activeFilter]);
    setPage(1);
  }, [dataLoading, fullDataset.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive Filter[] from pane state
  const paneFilters = useMemo((): Filter[] => {
    const out: Filter[] = [];
    if (pane.milestone.length) out.push({ id: 'pane-milestone', label: `Phase: ${pane.milestone.map(m => m.split(' - ')[0]).join(', ')}`, fn: r => pane.milestone.includes(String(r.milestone ?? '')) });
    if (pane.returnType.length) out.push({ id: 'pane-returntype', label: `Type: ${pane.returnType.join(', ')}`, fn: r => pane.returnType.includes(String(r.ReturnType ?? '')) });
    if (pane.dueDate.length) out.push({ id: 'pane-duedate', label: `Due: ${pane.dueDate.join(', ')}`, fn: r => pane.dueDate.includes(fmtDate(r.CurrentDueDate)) });
    if (pane.clientStatus.length) out.push({ id: 'pane-status', label: `Status: ${pane.clientStatus.join(', ')}`, fn: r => pane.clientStatus.includes(String(r.ClientStatus ?? '')) });
    if (pane.prepProcess.length) out.push({ id: 'pane-prep', label: `Prep: ${pane.prepProcess.join(', ')}`, fn: r => pane.prepProcess.includes(String(r.PrepProcess ?? '')) });
    if (pane.partnerSearch.trim()) out.push({ id: 'pane-partner', label: `Partner: "${pane.partnerSearch.trim()}"`, fn: r => String(r.JobPartner ?? '').toLowerCase().includes(pane.partnerSearch.trim().toLowerCase()) });
    if (pane.mselMissing)  out.push({ id: 'pane-msel',    label: 'MSEL Missing',  fn: r => r.MSEL === 'MISSING' });
    if (pane.sowMissing)   out.push({ id: 'pane-sow',     label: 'SOW Missing',   fn: r => r.SOW  === 'MISSING' });
    if (pane.accrualOnly)  out.push({ id: 'pane-accrual', label: 'Accrual Only',  fn: r => r.isAccrual === 'TRUE' });
    if (pane.vipOnly)      out.push({ id: 'pane-vip',     label: 'VIP Only',      fn: r => r.isVIP === 'TRUE' });
    if (pane.iddBeforeStatutory) out.push({
      id: 'pane-idd',
      label: 'IDD Before Statutory Due',
      fn: r => {
        const idd = fmtDate(r.IDD);
        const due = fmtDate(r.CurrentDueDate);
        return idd !== '—' && due !== '—' && idd < due;
      },
    });
    return out;
  }, [pane]);

  const allFilters = useMemo(() => [...defaultFilters, ...paneFilters], [defaultFilters, paneFilters]);

  const visibleRows = useMemo(() => {
    let rows = fullDataset;
    for (const f of allFilters) rows = rows.filter(f.fn);
    return rows;
  }, [fullDataset, allFilters]);

  const sortedRows = useMemo(() => {
    if (!sortCol) return visibleRows;
    return [...visibleRows].sort((a, b) => {
      const av = String(a[sortCol] ?? '');
      const bv = String(b[sortCol] ?? '');
      const cmp = av.localeCompare(bv, undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [visibleRows, sortCol, sortDir]);

  const pagedRows  = sortedRows.slice(0, page * PAGE_SIZE);
  const activeCols = ALL_COLS.filter(c => visibleColKeys.has(c.key));

  function removeFilter(id: string) {
    if (id.startsWith('pane-')) {
      const field = id.replace('pane-', '');
      const map: Record<string, Partial<PaneState>> = {
        milestone: { milestone: [] }, returntype: { returnType: [] }, duedate: { dueDate: [] },
        status: { clientStatus: [] }, prep: { prepProcess: [] }, partner: { partnerSearch: '' },
        msel: { mselMissing: false }, sow: { sowMissing: false },
        accrual: { accrualOnly: false }, vip: { vipOnly: false },
        idd: { iddBeforeStatutory: false },
      };
      if (map[field]) setPane(p => ({ ...p, ...map[field] }));
    } else {
      setDefaultFilters(prev => prev.filter(f => f.id !== id));
    }
    setPage(1);
  }

  function toggleSort(key: string) {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(key); setSortDir('asc'); }
    setPage(1);
  }

  // Phase — full label kept for filter fn; shortLabel for display
  const milestoneData = useMemo(() =>
    MILESTONE_ORDER
      .map(m => ({ label: m, short: m.split(' - ')[0], count: visibleRows.filter(r => r.milestone === m).length }))
      .filter(d => d.count > 0)
  , [visibleRows]);

  // Return type — collapse anything < 5% of total into "Other <5%", hide zeros
  const { returnTypeData, returnTypeOtherLabels } = useMemo(() => {
    const raw = groupCount(visibleRows, 'ReturnType', 100).filter(r => r.count > 0);
    const total = raw.reduce((s, r) => s + r.count, 0);
    if (total === 0) return { returnTypeData: [], returnTypeOtherLabels: [] as string[] };
    const main  = raw.filter(r => r.count / total >= 0.05);
    const small = raw.filter(r => r.count / total <  0.05);
    const smallTotal = small.reduce((s, r) => s + r.count, 0);
    const data = smallTotal > 0 ? [...main, { label: 'Other <5%', count: smallTotal }] : main;
    return { returnTypeData: data, returnTypeOtherLabels: small.map(r => r.label) };
  }, [visibleRows]);

  // Current staff — sorted most to least (reverse so highest bar is at top in horizontal chart)
  const staffData = useMemo(() =>
    groupCount(visibleRows, 'CurrentStaff', 11)
      .filter(d => d.label !== 'No Staff Allocated')
      .slice(0, 10)
      .reverse()
  , [visibleRows]);

  function addChartFilter(id: string, label: string, fn: (r: Row) => boolean) {
    setDefaultFilters(prev => [...prev.filter(f => f.id !== id), { id, label, fn }]);
    setPage(1);
  }

  const fetchInsights = useCallback(async () => {
    setInsightsLoading(true); setInsightsOpen(true); setInsightsHtml(null);
    try {
      const payload = buildInsightPayload(visibleRows, allFilters.map(f => f.label));
      const res = await fetch('/api/ai-chat', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: INSIGHTS_SYSTEM,
          messages: [{ role: 'user', content: `Dataset statistics:\n${JSON.stringify(payload, null, 2)}\n\nProvide your analysis as markdown.` }],
        }),
      });
      if (!res.ok) throw new Error(`AI error: ${res.status}`);
      const data = await res.json();
      const text = data.message || data.content?.[0]?.text || JSON.stringify(data);
      setInsightsHtml(renderMarkdown(text));
    } catch (e: unknown) {
      setInsightsHtml(`<p style="color:#ff6b6b">Error: ${e instanceof Error ? e.message : String(e)}</p>`);
    }
    setInsightsLoading(false);
  }, [visibleRows, allFilters]);

  function handlePrintInsights() {
    if (!insightsHtml) return;
    const filterSummary = allFilters.map(f => f.label).join(' · ') || 'No filters applied';
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Tax Return Analytics — AI Insights</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    @page { margin: 0.85in; size: letter; }
    body {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.65;
      color: #111;
      max-width: 7in;
      margin: 0 auto;
      padding: 0.5in 0;
    }
    .print-header { border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 20px; }
    .print-header h1 { font-size: 17pt; margin: 0 0 4px 0; font-weight: 700; }
    .print-meta { font-size: 9.5pt; color: #555; margin: 2px 0; }
    h2, h3 { font-family: Arial, Helvetica, sans-serif; }
    h2 { font-size: 14pt; margin: 22px 0 6px 0; padding-bottom: 4px; border-bottom: 1px solid #ddd; }
    h3 { font-size: 12pt; margin: 16px 0 4px 0; }
    h4, h5 { font-size: 11pt; margin: 12px 0 4px 0; font-family: Arial, Helvetica, sans-serif; }
    p { margin: 6px 0; }
    ul { margin: 6px 0 10px 0; padding-left: 22px; }
    li { margin: 3px 0; }
    strong { font-weight: 700; }
    em { font-style: italic; }
    code { font-family: 'Courier New', monospace; font-size: 9.5pt; background: #f4f4f4; padding: 1px 4px; border-radius: 3px; }
    hr { border: none; border-top: 1px solid #ddd; margin: 16px 0; }
    @media print {
      body { padding: 0; }
      a { color: inherit; text-decoration: none; }
    }
  </style>
</head>
<body>
  <div class="print-header">
    <h1>Tax Return Analytics — AI Insights</h1>
    <div class="print-meta">${dateStr}</div>
    <div class="print-meta"><strong>Filters:</strong> ${filterSummary}</div>
    <div class="print-meta"><strong>Population:</strong> ${visibleRows.length.toLocaleString()} returns</div>
  </div>
  ${insightsHtml}
</body>
</html>`);
    win.document.close();
    win.focus();
    win.print();
    win.addEventListener('afterprint', () => win.close());
  }

  function handleExport() {
    const cols = activeCols.map(c => c.key);
    const csv = [
      cols.join(','),
      ...visibleRows.map(row =>
        cols.map(c => {
          const s = row[c] == null ? '' : String(row[c]);
          return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g,'""')}"` : s;
        }).join(',')
      ),
    ].join('\n');
    const filename = `returns_export_${Date.now()}.csv`;
    fetch('/api/returns-export', { method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, csvData: csv }),
    }).catch(() => {});
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="rai-wrap">

      {/* Header */}
      <div className="rai-header">
        <div className="rai-header-left">
          <button className="btn" style={{ marginBottom: 6 }} onClick={() => navigate('/reports')}>
            <ArrowLeft size={14} /> Reports
          </button>
          <h1>Tax Return Analytics</h1>
          <div className="muted" style={{ fontSize: 13 }}>
            {dataLoading ? 'Loading dataset…' : dataError ? 'Error loading data' : `${fullDataset.length.toLocaleString()} total returns loaded`}
          </div>
        </div>
        <div className="rai-header-right">
          <button className={`btn${paneOpen ? ' active' : ''}`} onClick={() => setPaneOpen(o => !o)}>
            <SlidersHorizontal size={13} /> Filters
          </button>
          <button className="btn" onClick={() => reload()} title="Refresh data" disabled={dataLoading}>
            <RefreshCw size={13} />
          </button>
          <button className="btn" onClick={handleExport} disabled={dataLoading || visibleRows.length === 0}>
            <Download size={13} /> Export CSV
          </button>
          <button className="btn primary" onClick={fetchInsights} disabled={dataLoading || visibleRows.length === 0 || insightsLoading}>
            <Sparkles size={13} /> AI Insights
          </button>
        </div>
      </div>

      {/* Filter badges */}
      <div className="rai-filter-bar">
        <span className="rai-filter-label">Filters:</span>
        {allFilters.length === 0 && <span className="rai-filter-label">None — showing all {fullDataset.length.toLocaleString()} returns</span>}
        {allFilters.map(f => (
          <span key={f.id} className="rai-badge">
            {f.label}
            <button className="rai-badge-remove" onClick={() => removeFilter(f.id)}><X size={11} /></button>
          </span>
        ))}
        <span className="rai-record-count">{visibleRows.length.toLocaleString()} return{visibleRows.length !== 1 ? 's' : ''} shown</span>
      </div>

      {/* Body: optional filter pane + main content */}
      <div className={`rai-body${paneOpen ? ' has-pane' : ''}`}>
        {paneOpen && <FilterPane pane={pane} setPane={setPane} fullDataset={fullDataset} />}

        <div className="rai-main">
          {/* Charts */}
          {!dataLoading && visibleRows.length > 0 && (
            <div className="rai-charts-grid">
              {/* Phase — donut chart with gradient fills, click to filter */}
              <div className="rai-chart-card">
                <div className="rai-chart-title">By Phase</div>
                <div style={{ position: 'relative' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <defs>
                        {([
                          ['pg0', '#b0bac4', '#6b7884'],
                          ['pg1', '#a0baff', '#5a7edf'],
                          ['pg2', '#ffe496', '#d4961e'],
                          ['pg3', '#d8a4ff', '#9050d0'],
                          ['pg4', '#80eaaa', '#2fa862'],
                        ] as [string,string,string][]).map(([id, c1, c2]) => (
                          <linearGradient key={id} id={id} x1="0" y1="0" x2="0.5" y2="1">
                            <stop offset="0%"   stopColor={c1} />
                            <stop offset="100%" stopColor={c2} />
                          </linearGradient>
                        ))}
                      </defs>
                      <Pie
                        data={milestoneData}
                        dataKey="count"
                        nameKey="short"
                        cx="50%" cy="50%"
                        innerRadius={52}
                        outerRadius={82}
                        paddingAngle={2}
                        style={{ cursor: 'pointer' }}
                        onClick={(d: any) =>
                          addChartFilter('chart-phase', `Phase: ${d.short}`, r => r.milestone === d.label)
                        }
                        label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
                          if (percent < 0.08) return null;
                          const R = Math.PI / 180;
                          const r = innerRadius + (outerRadius - innerRadius) * 0.5;
                          return (
                            <text x={cx + r * Math.cos(-midAngle * R)} y={cy + r * Math.sin(-midAngle * R)}
                              fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}
                              style={{ textShadow: '0 1px 3px rgba(0,0,0,.6)' }}>
                              {`${(percent * 100).toFixed(0)}%`}
                            </text>
                          );
                        }}
                        labelLine={false}
                      >
                        {milestoneData.map((d) => {
                          const gradIds = ['pg0','pg1','pg2','pg3','pg4'];
                          const idx = MILESTONE_ORDER.indexOf(d.label);
                          return (
                            <Cell key={d.label}
                              fill={`url(#${gradIds[idx] ?? 'pg0'})`}
                              stroke="rgba(255,255,255,.06)"
                              strokeWidth={1}
                            />
                          );
                        })}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: 'rgba(15,20,28,0.92)', border: '1px solid rgba(122,162,255,0.25)', borderRadius: 10, fontSize: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)', padding: '8px 14px' }}
                        labelStyle={{ color: 'var(--muted)', fontWeight: 600, marginBottom: 4 }}
                        itemStyle={{ color: 'var(--text)' }}
                        formatter={(value: any, name: any) => [Number(value).toLocaleString(), name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Donut center label */}
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center', pointerEvents: 'none',
                  }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>
                      {milestoneData.reduce((s, d) => s + d.count, 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 3 }}>
                      returns
                    </div>
                  </div>
                </div>
              </div>

              {/* Return type — consolidated, click to filter */}
              <div className="rai-chart-card">
                <div className="rai-chart-title">By Return Type</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={returnTypeData} margin={{ top: 4, right: 8, left: -20, bottom: 20 }}>
                    <XAxis dataKey="label" tick={{ fill: 'var(--muted)', fontSize: 11 }} interval={0} />
                    <YAxis tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: 'rgba(15,20,28,0.92)', border: '1px solid rgba(122,162,255,0.25)', borderRadius: 10, fontSize: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)', padding: '8px 14px' }}
                      labelStyle={{ color: 'var(--muted)', fontWeight: 600, marginBottom: 4 }}
                      itemStyle={{ color: 'var(--text)' }}
                      cursor={{ fill: 'rgba(122,162,255,0.06)' }} />
                    <Bar dataKey="count" radius={[3,3,0,0]} style={{ cursor: 'pointer' }}
                      onClick={(d: any) => {
                        if (d.label === 'Other <5%') {
                          addChartFilter('chart-returntype', 'Type: Other <5%',
                            r => returnTypeOtherLabels.includes(String(r.ReturnType ?? '')));
                        } else {
                          addChartFilter(`chart-returntype`, `Type: ${d.label}`,
                            r => String(r.ReturnType ?? '') === d.label);
                        }
                      }}
                    >
                      {returnTypeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Current staff — horizontal bar, click to filter */}
              <div className="rai-chart-card">
                <div className="rai-chart-title">Returns by Current Staff</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={staffData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                    <XAxis type="number" tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                    <YAxis type="category" dataKey="label" tick={{ fill: 'var(--muted)', fontSize: 10 }} width={110} />
                    <Tooltip contentStyle={{ background: 'rgba(15,20,28,0.92)', border: '1px solid rgba(122,162,255,0.25)', borderRadius: 10, fontSize: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)', padding: '8px 14px' }}
                      labelStyle={{ color: 'var(--muted)', fontWeight: 600, marginBottom: 4 }}
                      itemStyle={{ color: 'var(--text)' }}
                      cursor={{ fill: 'rgba(122,162,255,0.06)' }} />
                    <Bar dataKey="count" fill="#57d38c" radius={[0,3,3,0]} style={{ cursor: 'pointer' }}
                      onClick={(d: any) =>
                        addChartFilter('chart-staff', `Staff: ${d.label}`, r => String(r.CurrentStaff ?? '') === d.label)
                      }
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* AI Insights panel */}
          {insightsOpen && (
            <div className="rai-insights-panel">
              <div className="rai-insights-header">
                <h3>AI Insights</h3>
                <div style={{ display: 'flex', gap: 6 }}>
                  {!insightsLoading && insightsHtml && (
                    <button className="btn" onClick={handlePrintInsights} title="Print to PDF">
                      <Printer size={13} /> Print
                    </button>
                  )}
                  <button className="btn" onClick={() => setInsightsOpen(false)}><X size={13} /> Close</button>
                </div>
              </div>
              {insightsLoading
                ? <div className="rai-thinking">Analyzing {visibleRows.length.toLocaleString()} returns…</div>
                : <div className="rai-insights-content" dangerouslySetInnerHTML={{ __html: insightsHtml ?? '' }} />}
            </div>
          )}

          {dataError && (
            <div style={{ color: '#ff6b6b', fontSize: 13 }}>
              Failed to load dataset: {dataError} — <button className="btn" onClick={() => reload()}>Retry</button>
            </div>
          )}

          {/* Table */}
          {!dataLoading && visibleRows.length > 0 && (
            <div className="rai-table-section">
              <div className="rai-table-controls">
                <span className="rai-table-info">
                  Showing {pagedRows.length.toLocaleString()} of {sortedRows.length.toLocaleString()} returns
                  {sortCol && <> · sorted by <strong>{sortCol}</strong> {sortDir === 'asc' ? '↑' : '↓'}</>}
                </span>
                <div style={{ position: 'relative' }}>
                  <button className="btn" onClick={() => setColChooserOpen(o => !o)}>
                    <Columns size={13} /> Columns ({visibleColKeys.size})
                  </button>
                  {colChooserOpen && (
                    <ColChooser visibleKeys={visibleColKeys} setVisibleKeys={setVisibleColKeys}
                      onClose={() => setColChooserOpen(false)} />
                  )}
                </div>
              </div>
              <div className="rai-table-wrap">
                <table className="rai-table">
                  <thead>
                    <tr>
                      {activeCols.map(col => (
                        <th key={col.key} className={`sortable${sortCol === col.key ? ' sorted' : ''}`}
                          onClick={() => toggleSort(col.key)}>
                          {col.label}{sortCol === col.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, i) => (
                      <tr key={i}>
                        {activeCols.map(col => (
                          <td key={col.key}>{col.render ? col.render(row) : String(row[col.key] ?? '—')}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagedRows.length < sortedRows.length && (
                <button className="rai-load-more" onClick={() => setPage(p => p + 1)}>
                  Load more ({(sortedRows.length - pagedRows.length).toLocaleString()} remaining)
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
